import React, { useState, useEffect } from 'react'
import { HeroContainer, HeroLeftContainer, HeroRightContainer, HeroInnerContainer, TextLoop, Title, Span, SubTitle, ResumeButton, CoverLetterButton, ButtonGroup, AvailabilityBadge } from './HeroStyle'
import ReactGA from 'react-ga4'
import styled, { keyframes } from 'styled-components';
import dp1 from '../../images/1.png'
import dp2 from '../../images/2.jpg'
import dp3 from '../../images/3.jpg'
import dp4 from '../../images/4.jpg'
import dp5 from '../../images/5.jpg'
import dp6 from '../../images/6.jpg'
import dp7 from '../../images/7.jpg'
import dp8 from '../../images/8.jpg'
import dp9 from '../../images/9.jpg'
import dp10 from '../../images/10.png'
import { Bio } from '../../data/constants';
import { motion } from 'framer-motion'

const GlowAnimation = keyframes`
  0% {
    box-shadow: 0 0 15px rgba(0, 0, 255, 0.3);
  }
  50% {
    box-shadow: 0 0 30px rgba(0, 0, 255, 0.6);
  }
  100% {
    box-shadow: 0 0 15px rgba(0, 0, 255, 0.3);
  }
`;

const FloatAnimation = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
`;

const FloatingDotsContainer = styled.div`
  position: relative;
  display: inline-block;
  width: 400px;
  height: 400px;
  flex-shrink: 0;
`;

const FloatingDot = styled.div`
  position: absolute;
  width: 8px;
  height: 8px;
  background: ${({ theme }) => theme.primary};
  border-radius: 50%;
  animation: ${FloatAnimation} 3s ease-in-out infinite;

  &:nth-child(1) {
    top: 20%;
    left: -20px;
    animation-delay: 0s;
  }

  &:nth-child(2) {
    top: 10%;
    right: -15px;
    animation-delay: 0.5s;
  }

  &:nth-child(3) {
    bottom: 30%;
    left: -25px;
    animation-delay: 1s;
  }

  &:nth-child(4) {
    bottom: 20%;
    right: -20px;
    animation-delay: 1.5s;
  }

  &:nth-child(5) {
    top: 50%;
    left: -30px;
    animation-delay: 2s;
  }

  &:nth-child(6) {
    top: 60%;
    right: -25px;
    animation-delay: 2.5s;
  }
`;

// Styled component for the slideshow images
const SlideshowImg = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  object-position: center top;
  border: 1px solid ${({ theme }) => theme.primary};
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  opacity: ${({ $active }) => ($active ? 1 : 0)};
  transition: opacity 0.15s ease-in-out, transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.5), 0 0 20px rgba(0, 0, 255, 0.6);
    animation: ${GlowAnimation} 2s infinite;
  }
`;

const TypewriterText = ({ texts }) => {
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [currentText, setCurrentText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const fullText = texts[currentTextIndex];

      if (isDeleting) {
        setCurrentText(fullText.substring(0, currentText.length - 1));
      } else {
        setCurrentText(fullText.substring(0, currentText.length + 1));
      }

      if (!isDeleting && currentText === fullText) {
        setTimeout(() => setIsDeleting(true), 1000);
      } else if (isDeleting && currentText === '') {
        setIsDeleting(false);
        setCurrentTextIndex((prevIndex) => (prevIndex + 1) % texts.length);
      }
    }, isDeleting ? 50 : 100);

    return () => clearTimeout(timeout);
  }, [currentText, isDeleting, currentTextIndex, texts]);

  return <span>{currentText}</span>;
};


const slideshowImages = [dp1, dp2, dp3, dp4, dp5, dp6, dp7, dp8, dp9, dp10];

const HeroSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % slideshowImages.length);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div id="about">
      <HeroContainer>

        <HeroInnerContainer>
          <HeroLeftContainer
            as={motion.div}
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            id="Left"
          >
            {Bio.availableForOpportunities && (
              <AvailabilityBadge>Available for opportunities</AvailabilityBadge>
            )}
            <Title>Hello, I am <br /> {Bio.name}</Title>
            <TextLoop style={{
              height: '80px',
              display: 'flex',
              alignItems: 'center',
              overflow: 'hidden'
            }}>
              <Span>
                <TypewriterText texts={['Senior Data Science Analyst', 'Software Developer Engineer', 'Freelancer']} />
              </Span>
            </TextLoop>
            <SubTitle>{Bio.description}</SubTitle>
            <ButtonGroup>
              <ResumeButton href='/Urvansh_Shah_Resume.pdf' target='_blank' onClick={() => ReactGA.event({ category: 'CTA', action: 'resume_download', label: 'hero_section' })}>My Resume</ResumeButton>
              <CoverLetterButton href='/URVANSH_SHAH_COVER_LETTER_LATEST.pdf' target='_blank' onClick={() => ReactGA.event({ category: 'CTA', action: 'cover_letter_download', label: 'hero_section' })}>Cover Letter</CoverLetterButton>
            </ButtonGroup>
          </HeroLeftContainer>

          <HeroRightContainer
            as={motion.div}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            id="Right"
          >
            <FloatingDotsContainer>
              <FloatingDot />
              <FloatingDot />
              <FloatingDot />
              <FloatingDot />
              {slideshowImages.map((img, i) => (
                <SlideshowImg
                  key={i}
                  src={img}
                  alt="hero-image"
                  $active={currentIndex === i}
                />
              ))}
            </FloatingDotsContainer>
          </HeroRightContainer>
        </HeroInnerContainer>

      </HeroContainer>
    </div>
  )
}

export default HeroSection